import { useEffect, useState } from 'react';
import { useAppContext } from '@/App';
import { getCourses } from '@/lib/api';
import AccountSettingsCard from '@/components/profile/AccountSettingsCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { BookOpen, Users, Mail, Briefcase, FileText } from 'lucide-react';

export default function InstructorProfile() {
  const { currentInstructor } = useAppContext();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await getCourses();
        const all = res.data || [];
        const mine = all.filter(
          (c) => c.instructor_id === currentInstructor?.id,
        );
        setCourses(mine);
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, [currentInstructor]);

  if (!currentInstructor) {
    return (
      <div className="space-y-6 animate-fade-in">
        <AccountSettingsCard />
        <p className="text-[#062B1A]/60">Instructor profile not found.</p>
      </div>
    );
  }

  const instructor = currentInstructor;
  const totalEnrolled = courses.reduce(
    (sum, c) => sum + (c.enrolled_students?.length || 0),
    0,
  );

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Profile Header */}
      <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm overflow-hidden">
        <div className="h-24 bg-gradient-to-r from-[#062B1A] to-[#2D4470]" />
        <CardContent className="relative pb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4 -mt-12">
            <Avatar className="w-24 h-24 border-4 border-white shadow-md">
              {instructor.photo && (
                <AvatarImage src={instructor.photo} alt={instructor.name} />
              )}
              <AvatarFallback className="bg-[#D4AF37] text-[#062B1A] text-2xl font-bold">
                {instructor.name
                  ?.split(' ')
                  .map((p) => p[0])
                  .join('') || '?'}
              </AvatarFallback>
            </Avatar>
            <div className="space-y-1 pt-2">
              <h2
                className="text-2xl font-semibold text-[#062B1A]"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                {instructor.name}
              </h2>
              <div className="flex items-center gap-2 text-sm text-[#062B1A]/60">
                <Mail className="w-4 h-4" /> {instructor.email}
              </div>
              {instructor.department && (
                <div className="flex items-center gap-2 text-sm text-[#062B1A]/60">
                  <Briefcase className="w-4 h-4" /> {instructor.department}
                </div>
              )}
              <Badge
                variant="outline"
                className="border-[rgba(6, 43, 26,0.20)] bg-[rgba(6, 43, 26,0.06)] text-[#062B1A]"
              >
                Instructor
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <AccountSettingsCard />

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">
                Courses
              </span>
              <BookOpen className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <p
              className="text-3xl font-bold text-[#062B1A]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {courses.length}
            </p>
          </CardContent>
        </Card>

        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">
                Total Enrolled Students
              </span>
              <Users className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <p
              className="text-3xl font-bold text-[#062B1A]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {totalEnrolled}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Bio */}
      {instructor.bio && (
        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle
              className="text-base text-[#062B1A] flex items-center gap-2"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              <FileText className="w-4 h-4" /> Bio
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-[#062B1A]/80 leading-relaxed whitespace-pre-wrap">
              {instructor.bio}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Courses List */}
      <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle
            className="text-base text-[#062B1A] flex items-center gap-2"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            <BookOpen className="w-4 h-4" /> My Courses
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div
                  key={i}
                  className="h-14 bg-[#F7F8FA] animate-pulse rounded-lg"
                />
              ))}
            </div>
          ) : courses.length === 0 ? (
            <p className="text-sm text-[#062B1A]/50">
              No courses assigned yet.
            </p>
          ) : (
            <div className="space-y-3">
              {courses.map((course) => (
                <div
                  key={course.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-[rgba(6, 43, 26,0.06)] bg-[rgba(6, 43, 26,0.02)]"
                >
                  <div>
                    <p className="text-sm font-medium text-[#062B1A]">
                      {course.title}
                    </p>
                    {course.category && (
                      <p className="text-xs text-[#062B1A]/50">
                        {course.category}
                      </p>
                    )}
                  </div>
                  <Badge
                    variant="outline"
                    className="border-[rgba(6, 43, 26,0.12)] bg-[rgba(6, 43, 26,0.04)] text-[#062B1A]/70"
                  >
                    <Users className="w-3 h-3 mr-1" />
                    {course.enrolled_students?.length || 0} enrolled
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
